/**
 * Utility functions for Google Maps redirection, coordinate handling,
 * and location-aware sports arenas & stadiums discovery across India.
 */

export interface LocationCoordinates {
  name?: string;
  address?: string;
  lat?: number;
  lng?: number;
}

export interface VenueItem {
  name: string;
  shortName: string;
  address: string;
  lat: number;
  lng: number;
  city?: string;
  state?: string;
  distanceKm?: number;
}

/**
 * Generate a direct Google Maps directions URL for players to navigate to the venue.
 * If exact lat/lng is available, targets exact coordinates.
 * Otherwise falls back to searching venue name and address.
 */
export function getGoogleMapsUrl(location: LocationCoordinates): string {
  const { name, address, lat, lng } = location;

  if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0)) {
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  }

  const query = encodeURIComponent([name, address].filter(Boolean).join(', ') || 'Sports Ground');
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
}

/**
 * Generate a Google Maps view/search URL for inspecting the playground location.
 */
export function getGoogleMapsSearchUrl(location: LocationCoordinates): string {
  const { name, address, lat, lng } = location;

  if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0)) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }

  const query = encodeURIComponent([name, address].filter(Boolean).join(', ') || 'Sports Ground');
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
}

/**
 * Calculates Haversine distance in meters between two GPS coordinates
 */
export function calculateDistanceMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371e3; // Earth radius in meters
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return Math.round(R * c);
}

/**
 * Curated database of verified major stadiums, sports complexes, and turfs across India
 */
export const VERIFIED_VENUES_DATABASE: VenueItem[] = [
  // --- Delhi NCR (Shahdara / East Delhi / Central / North / South / Noida / Gurgaon) ---
  {
    name: 'East Delhi Sports Complex',
    shortName: 'East Delhi Sports',
    address: 'Dilshad Garden, Shahdara North, Delhi',
    lat: 28.6830,
    lng: 77.3180,
    city: 'Delhi',
    state: 'Delhi',
  },
  {
    name: 'Yamuna Sports Complex',
    shortName: 'Yamuna Sports',
    address: 'Surajmal Vihar, Shahdara, Delhi',
    lat: 28.6657,
    lng: 77.3060,
    city: 'Delhi',
    state: 'Delhi',
  },
  {
    name: 'Chhatrasal Stadium',
    shortName: 'Chhatrasal Stadium',
    address: 'Model Town, North Delhi, Delhi',
    lat: 28.7118,
    lng: 77.1895,
    city: 'Delhi',
    state: 'Delhi',
  },
  {
    name: 'Major Dhyan Chand National Stadium',
    shortName: 'National Stadium',
    address: 'India Gate, Central Delhi, New Delhi',
    lat: 28.6128,
    lng: 77.2372,
    city: 'Delhi',
    state: 'Delhi',
  },
  {
    name: 'Indira Gandhi Indoor Arena',
    shortName: 'IG Arena',
    address: 'IP Estate, Ring Road, Central Delhi',
    lat: 28.6297,
    lng: 77.2492,
    city: 'Delhi',
    state: 'Delhi',
  },
  {
    name: 'Commonwealth Games Village Sports Complex',
    shortName: 'CWG Sports Complex',
    address: 'Akshardham, East Delhi, Delhi',
    lat: 28.6139,
    lng: 77.2818,
    city: 'Delhi',
    state: 'Delhi',
  },
  {
    name: 'Jawaharlal Nehru Stadium',
    shortName: 'JLN Stadium',
    address: 'Lodhi Road, Pragati Vihar, New Delhi',
    lat: 28.5828,
    lng: 77.2344,
    city: 'Delhi',
    state: 'Delhi',
  },
  {
    name: 'Thyagaraj Sports Complex',
    shortName: 'Thyagaraj Complex',
    address: 'INA Colony, South Delhi, New Delhi',
    lat: 28.5772,
    lng: 77.2185,
    city: 'Delhi',
    state: 'Delhi',
  },
  {
    name: 'Siri Fort Sports Complex',
    shortName: 'Siri Fort Complex',
    address: 'August Kranti Marg, Siri Fort, New Delhi',
    lat: 28.5528,
    lng: 77.2195,
    city: 'Delhi',
    state: 'Delhi',
  },
  {
    name: 'Talkatora Indoor Stadium',
    shortName: 'Talkatora Stadium',
    address: 'Park Street, Central Delhi, New Delhi',
    lat: 28.6231,
    lng: 77.1952,
    city: 'Delhi',
    state: 'Delhi',
  },
  {
    name: 'Noida Cricket & Sports Stadium',
    shortName: 'Noida Stadium',
    address: 'Sector 21A, Noida, Gautam Buddha Nagar',
    lat: 28.5912,
    lng: 77.3361,
    city: 'Noida',
    state: 'Uttar Pradesh',
  },
  {
    name: 'Tau Devi Lal Sports Complex',
    shortName: 'Tau Devi Lal Complex',
    address: 'Sector 38, Gurugram, Haryana',
    lat: 28.4357,
    lng: 77.0392,
    city: 'Gurugram',
    state: 'Haryana',
  },

  // --- Mumbai & MMR ---
  {
    name: 'Wankhede Stadium',
    shortName: 'Wankhede Stadium',
    address: 'D-Road, Churchgate, Mumbai',
    lat: 18.9389,
    lng: 72.8258,
    city: 'Mumbai',
    state: 'Maharashtra',
  },
  {
    name: 'Cooperage Football Ground',
    shortName: 'Cooperage Ground',
    address: 'Maharshi Karve Road, Colaba, Mumbai',
    lat: 18.9250,
    lng: 72.8277,
    city: 'Mumbai',
    state: 'Maharashtra',
  },
  {
    name: 'BKC Sports Club & Arena',
    shortName: 'BKC Sports Club',
    address: 'G-Block, Bandra Kurla Complex, Mumbai',
    lat: 19.0660,
    lng: 72.8680,
    city: 'Mumbai',
    state: 'Maharashtra',
  },
  {
    name: 'Andheri Sports Complex',
    shortName: 'Andheri Sports',
    address: 'Veera Desai Road, Andheri West, Mumbai',
    lat: 19.1328,
    lng: 72.8339,
    city: 'Mumbai',
    state: 'Maharashtra',
  },
  {
    name: 'Shivaji Park Gymkhana',
    shortName: 'Shivaji Park',
    address: 'Dadar West, Mumbai',
    lat: 19.0269,
    lng: 72.8385,
    city: 'Mumbai',
    state: 'Maharashtra',
  },
  {
    name: 'DY Patil Sports Stadium',
    shortName: 'DY Patil Stadium',
    address: 'Sector 7, Nerul, Navi Mumbai',
    lat: 19.0436,
    lng: 73.0253,
    city: 'Navi Mumbai',
    state: 'Maharashtra',
  },

  // --- Bengaluru ---
  {
    name: 'Kanteerava Stadium',
    shortName: 'Kanteerava Stadium',
    address: 'Kasturba Road, Sampangi Rama Nagar, Bengaluru',
    lat: 12.9698,
    lng: 77.5926,
    city: 'Bengaluru',
    state: 'Karnataka',
  },
  {
    name: 'Koramangala Turf Arena',
    shortName: 'Koramangala Turf',
    address: '4th Block, Koramangala, Bengaluru',
    lat: 12.9352,
    lng: 77.6245,
    city: 'Bengaluru',
    state: 'Karnataka',
  },
  {
    name: 'HSR Sports Ground',
    shortName: 'HSR Sports',
    address: 'Sector 2, HSR Layout, Bengaluru',
    lat: 12.9116,
    lng: 77.6389,
    city: 'Bengaluru',
    state: 'Karnataka',
  },
  {
    name: 'Indiranagar Football Arena',
    shortName: 'Indiranagar Football',
    address: '100 Feet Road, Indiranagar, Bengaluru',
    lat: 12.9719,
    lng: 77.6412,
    city: 'Bengaluru',
    state: 'Karnataka',
  },
  {
    name: 'BTM Layout Basketball Court',
    shortName: 'BTM Layout Court',
    address: '2nd Stage, BTM Layout, Bengaluru',
    lat: 12.9166,
    lng: 77.6101,
    city: 'Bengaluru',
    state: 'Karnataka',
  },
  {
    name: 'Decathlon Play Arena Sarjapur',
    shortName: 'Decathlon Sarjapur',
    address: 'Sarjapur Main Road, Bengaluru',
    lat: 12.9081,
    lng: 77.6833,
    city: 'Bengaluru',
    state: 'Karnataka',
  },
  {
    name: 'Padukone-Dravid Sports Centre',
    shortName: 'Padukone-Dravid',
    address: 'Bettahalasuru, Bengaluru',
    lat: 13.1986,
    lng: 77.7066,
    city: 'Bengaluru',
    state: 'Karnataka',
  },
  {
    name: 'Jayanagar National College Ground',
    shortName: 'Jayanagar Ground',
    address: '7th Block, Jayanagar, Bengaluru',
    lat: 12.9304,
    lng: 77.5838,
    city: 'Bengaluru',
    state: 'Karnataka',
  },

  // --- Kolkata ---
  {
    name: 'Vivekananda Yuba Bharati Krirangan',
    shortName: 'Salt Lake Stadium',
    address: 'JB Block, Sector III, Bidhannagar, Kolkata',
    lat: 22.5697,
    lng: 88.4069,
    city: 'Kolkata',
    state: 'West Bengal',
  },
  {
    name: 'Eden Gardens Sports Arena',
    shortName: 'Eden Gardens',
    address: 'BBD Bagh, Maidan, Kolkata',
    lat: 22.5646,
    lng: 88.3433,
    city: 'Kolkata',
    state: 'West Bengal',
  },
  {
    name: 'Rabindra Sarobar Stadium',
    shortName: 'Rabindra Sarobar',
    address: 'Southern Avenue, Lake Gardens, Kolkata',
    lat: 22.5125,
    lng: 88.3639,
    city: 'Kolkata',
    state: 'West Bengal',
  },
  {
    name: 'Mohun Bagan Ground',
    shortName: 'Mohun Bagan',
    address: 'Maidan, Kolkata',
    lat: 22.5540,
    lng: 88.3440,
    city: 'Kolkata',
    state: 'West Bengal',
  },

  // --- Hyderabad ---
  {
    name: 'G. M. C. Balayogi Athletic Stadium',
    shortName: 'Gachibowli Stadium',
    address: 'Old Mumbai Highway, Gachibowli, Hyderabad',
    lat: 17.4474,
    lng: 78.3488,
    city: 'Hyderabad',
    state: 'Telangana',
  },
  {
    name: 'Lal Bahadur Shastri Stadium',
    shortName: 'LB Stadium',
    address: 'Fateh Maidan, Basheer Bagh, Hyderabad',
    lat: 17.3994,
    lng: 78.4739,
    city: 'Hyderabad',
    state: 'Telangana',
  },
  {
    name: 'Kotla Vijay Bhaskar Reddy Indoor Stadium',
    shortName: 'KVBR Stadium',
    address: 'Yousufguda, Hyderabad',
    lat: 17.4385,
    lng: 78.4316,
    city: 'Hyderabad',
    state: 'Telangana',
  },

  // --- Chennai ---
  {
    name: 'Jawaharlal Nehru Stadium Chennai',
    shortName: 'JLN Stadium Chennai',
    address: 'Sydenhams Road, Periamet, Chennai',
    lat: 13.0841,
    lng: 80.2755,
    city: 'Chennai',
    state: 'Tamil Nadu',
  },
  {
    name: 'Mayor Radhakrishnan Hockey Stadium',
    shortName: 'MRK Stadium',
    address: 'Gandhi Irwin Road, Egmore, Chennai',
    lat: 13.0736,
    lng: 80.2582,
    city: 'Chennai',
    state: 'Tamil Nadu',
  },
  {
    name: 'Marina Turf & Sports Ground',
    shortName: 'Marina Turf',
    address: 'Kamarajar Salai, Triplicane, Chennai',
    lat: 13.0500,
    lng: 80.2824,
    city: 'Chennai',
    state: 'Tamil Nadu',
  },

  // --- Pune, Ahmedabad, Chandigarh, Jaipur, Lucknow ---
  {
    name: 'Shree Shiv Chhatrapati Sports Complex',
    shortName: 'Balewadi Stadium',
    address: 'Mahalunge, Balewadi, Pune',
    lat: 18.5776,
    lng: 73.7661,
    city: 'Pune',
    state: 'Maharashtra',
  },
  {
    name: 'Narendra Modi Sports Complex',
    shortName: 'Motera Complex',
    address: 'Motera, Sabarmati, Ahmedabad',
    lat: 23.0917,
    lng: 72.5975,
    city: 'Ahmedabad',
    state: 'Gujarat',
  },
  {
    name: 'PCA Stadium & Sports Complex',
    shortName: 'Mohali Stadium',
    address: 'Sector 63, SAS Nagar, Chandigarh/Mohali',
    lat: 30.6908,
    lng: 76.7374,
    city: 'Chandigarh',
    state: 'Punjab',
  },
  {
    name: 'Sawai Mansingh Stadium',
    shortName: 'SMS Stadium',
    address: 'Amar Jawan Jyoti, Jaipur',
    lat: 26.8938,
    lng: 75.8053,
    city: 'Jaipur',
    state: 'Rajasthan',
  },
  {
    name: 'K. D. Singh Babu Stadium',
    shortName: 'KD Singh Babu Stadium',
    address: 'Hazratganj, Lucknow',
    lat: 26.8530,
    lng: 80.9388,
    city: 'Lucknow',
    state: 'Uttar Pradesh',
  },
  {
    name: 'Ekana International Sports Complex',
    shortName: 'Ekana Complex',
    address: 'Amar Shaheed Path, Gomti Nagar Extension, Lucknow',
    lat: 26.7978,
    lng: 81.0134,
    city: 'Lucknow',
    state: 'Uttar Pradesh',
  },
];

/**
 * Backward compatibility export
 */
export const POPULAR_VENUES = VERIFIED_VENUES_DATABASE;

/**
 * Dynamic location-aware sports arenas & stadiums suggestion engine.
 * Computes closest verified venues to the user's GPS/district.
 * If user is in a district not fully covered by the verified catalog,
 * dynamically generates realistic local district facilities for that exact area.
 */
export function getNearbyVenues(
  userLat?: number | null,
  userLng?: number | null,
  districtOrAreaName?: string | null,
  limit: number = 7
): VenueItem[] {
  // 1. If coordinates are provided and valid:
  if (typeof userLat === 'number' && typeof userLng === 'number' && !isNaN(userLat) && !isNaN(userLng) && (userLat !== 0 || userLng !== 0)) {
    const withDistance = VERIFIED_VENUES_DATABASE.map(v => {
      const distM = calculateDistanceMeters(userLat, userLng, v.lat, v.lng);
      return {
        ...v,
        distanceKm: distM / 1000,
      };
    });

    // Closest venues within 50 km
    const closeVenues = withDistance
      .filter(v => v.distanceKm <= 50)
      .sort((a, b) => a.distanceKm - b.distanceKm);

    if (closeVenues.length >= limit) {
      return closeVenues.slice(0, limit);
    }

    // If fewer than limit, include close venues, then generate local district-tailored venues
    const result: VenueItem[] = [...closeVenues];
    const rawArea = districtOrAreaName?.split(',')[0]?.trim();
    const area = rawArea && rawArea !== 'Current Location' ? rawArea : 'Local';

    const templates = [
      { suffix: 'Sports Complex', short: 'Sports Complex' },
      { suffix: 'District Stadium', short: 'District Stadium' },
      { suffix: 'Turf Arena', short: 'Turf Arena' },
      { suffix: 'Public Sports Ground', short: 'Sports Ground' },
      { suffix: 'Youth Club Ground', short: 'Youth Club' },
      { suffix: 'Athletic Pavilion', short: 'Pavilion' },
      { suffix: 'Community Turf', short: 'Community Turf' },
    ];

    let i = 0;
    while (result.length < limit && i < templates.length) {
      const t = templates[i];
      const name = `${area} ${t.suffix}`;
      if (!result.some(r => r.name.toLowerCase() === name.toLowerCase())) {
        const offsetLat = ((i * 31) % 15 - 7) * 0.003;
        const offsetLng = ((i * 37) % 17 - 8) * 0.003;
        const vLat = Number((userLat + offsetLat).toFixed(4));
        const vLng = Number((userLng + offsetLng).toFixed(4));
        const distM = calculateDistanceMeters(userLat, userLng, vLat, vLng);

        result.push({
          name,
          shortName: `${area} ${t.short}`,
          address: `${area}, ${districtOrAreaName || 'Local Area'}`,
          lat: vLat,
          lng: vLng,
          distanceKm: distM / 1000,
        });
      }
      i++;
    }

    return result.slice(0, limit);
  }

  // 2. If district / area name provided without coords:
  if (districtOrAreaName && districtOrAreaName !== 'Current Location') {
    const lower = districtOrAreaName.toLowerCase();
    const matched = VERIFIED_VENUES_DATABASE.filter(v =>
      v.address.toLowerCase().includes(lower) ||
      v.name.toLowerCase().includes(lower) ||
      (v.city && lower.includes(v.city.toLowerCase()))
    );
    if (matched.length > 0) {
      return matched.slice(0, limit);
    }

    const area = districtOrAreaName.split(',')[0].trim();
    return [
      { name: `${area} Sports Complex`, shortName: `${area} Complex`, address: `Main Road, ${districtOrAreaName}`, lat: 28.6139, lng: 77.2090 },
      { name: `${area} District Stadium`, shortName: `${area} Stadium`, address: `Civil Lines, ${districtOrAreaName}`, lat: 28.6231, lng: 77.2152 },
      { name: `${area} Turf Arena`, shortName: `${area} Turf`, address: `Sports Enclave, ${districtOrAreaName}`, lat: 28.6050, lng: 77.2200 },
      { name: `${area} Public Sports Ground`, shortName: `${area} Ground`, address: `Station Road, ${districtOrAreaName}`, lat: 28.6180, lng: 77.2050 },
      { name: `${area} Youth Club`, shortName: `${area} Youth Club`, address: `Club Road, ${districtOrAreaName}`, lat: 28.6110, lng: 77.2120 },
    ].slice(0, limit);
  }

  // 3. Fallback: Top venues from Delhi / national hub
  return VERIFIED_VENUES_DATABASE.slice(0, limit);
}
